import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain, Users, Zap, Building2, Heart, Wrench, Truck, ArrowRight } from "lucide-react";
import heroImage from "@/assets/hero-ai-assistant.jpg";
import healthcareImage from "@/assets/healthcare-ai.jpg";
import tradesImage from "@/assets/trades-ai.jpg";
import businessImage from "@/assets/business-ai.jpg";
import logisticsImage from "@/assets/logistics-ai.jpg";

const Home = () => {
  const industries = [
    {
      icon: Heart,
      title: "Healthcare",
      description: "Streamline patient communication, appointment scheduling, and administrative workflows with intelligent AI assistants.",
      image: healthcareImage,
    },
    {
      icon: Wrench,
      title: "Skilled Trades",
      description: "Manage customer inquiries, job scheduling, and follow-ups efficiently while you focus on delivering quality work.",
      image: tradesImage,
    },
    {
      icon: Building2,
      title: "Small Business",
      description: "Scale your operations with AI-powered customer service, lead generation, and administrative support.",
      image: businessImage,
    },
    {
      icon: Truck,
      title: "Distribution",
      description: "Optimize supply chain operations, shipment tracking, and customer communications with AI-powered automation.",
      image: logisticsImage,
    },
  ];

  const capabilities = [
    {
      icon: Brain,
      title: "Intelligent Automation",
      description: "Handle routine tasks automatically while learning from every interaction to improve over time.",
    },
    {
      icon: Users,
      title: "Customer Service",
      description: "Provide 24/7 support with human-like conversations that build trust and satisfaction.",
    },
    {
      icon: Zap,
      title: "Workflow Integration",
      description: "Seamlessly connect with your existing tools and systems for maximum efficiency.",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Empowering Businesses with{" "}
                <span className="text-accent">AI Virtual Employees</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Transform your operations with intelligent assistants that handle customer service, scheduling, communication, and workflow automation—helping you scale efficiently.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold">
                  <Link to="/contact">
                    Schedule a Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/services">See How AI Works for You</Link>
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in">
              <img
                src={heroImage}
                alt="AI Virtual Assistant Technology"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              What Virtual Employees Can Do
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Harness the power of AI to automate tasks, enhance customer experiences, and free your team to focus on what matters most.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <Card key={index} className="border-2 hover:border-accent transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <capability.icon className="w-6 h-6 text-accent" />
                  </div>
                  <CardTitle>{capability.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {capability.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tailored AI solutions designed specifically for your industry's unique needs and challenges.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="border-2 hover:border-accent transition-all duration-300 hover:shadow-lg overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={`${industry.title} AI Solutions`}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <industry.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>{industry.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {industry.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Meet Your AI Employee?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Discover how AI Virtual Employees can transform your business operations and help you scale efficiently.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold">
            <Link to="/contact">
              Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
